import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../reducers';
import { FakeAgentType } from './aiAgentsConsts';
import { AgentInputMode } from './agent-input/agentInputModeTypes';

//This reducer holds only UI related state

export interface AgentsHomeState {
  isExpanded: boolean;
  isCreateAgentActive: boolean;
  activeFakeAgentType?: FakeAgentType; //fake agents don't have ids
  advancedMode: AgentInputMode; // Session-scoped mode (resets on page refresh)
  selectedAdditionalOrgIds: string[]; // Multi-org mode: selected additional org IDs
}

const initialState: AgentsHomeState = {
  isExpanded: true,
  isCreateAgentActive: false,
  activeFakeAgentType: undefined,
  advancedMode: AgentInputMode.None,
  selectedAdditionalOrgIds: [],
};

export const agentsHomeSlice = createSlice({
  name: 'agentsHome',
  initialState,
  reducers: {
    setExpandMode: (state, action: PayloadAction<{ isExpanded: boolean }>) => {
      const { isExpanded } = action.payload;
      state.isExpanded = isExpanded;
    },
    toggleExpandMode: (state) => {
      state.isExpanded = !state.isExpanded;
    },
    setIsCreateActive: (state, action: PayloadAction<{ isActive: boolean }>) => {
      const { isActive } = action.payload;
      state.isCreateAgentActive = isActive;
    },
    resetAgentsHomeState: () => initialState,
    setActiveFakeAgent: (state, action: PayloadAction<{ agentType: FakeAgentType }>) => {
      const { agentType } = action.payload;
      state.activeFakeAgentType = agentType;
    },
    resetActiveFakeAgent: (state) => {
      state.activeFakeAgentType = undefined;
    },
    setAdvancedMode: (state, action: PayloadAction<{ advancedMode: AgentInputMode }>) => {
      state.advancedMode = action.payload.advancedMode;
      // Clear selected orgs when disabling multi-org mode
      if (action.payload.advancedMode !== AgentInputMode.MultiOrgMode) {
        state.selectedAdditionalOrgIds = [];
      }
    },
    setSelectedAdditionalOrgIds: (state, action: PayloadAction<{ orgIds: string[] }>) => {
      state.selectedAdditionalOrgIds = action.payload.orgIds;
    },
    clearSelectedAdditionalOrgIds: (state) => {
      state.selectedAdditionalOrgIds = [];
    },
  },
});

export const {
  toggleExpandMode,
  setIsCreateActive,
  resetAgentsHomeState,
  setExpandMode,
  setActiveFakeAgent,
  resetActiveFakeAgent,
  setAdvancedMode,
  setSelectedAdditionalOrgIds,
  clearSelectedAdditionalOrgIds,
} = agentsHomeSlice.actions;

export const selectIsExpandedAiChats = (state: RootState) => state.agentsHome.isExpanded;
export const selectIsCreateActive = (state: RootState) => state.agentsHome.isCreateAgentActive;
export const selectActiveFakeAgentType = (state: RootState) => {
  return state.agentsHome.activeFakeAgentType;
};
export const selectAdvancedMode = (state: RootState) => {
  return state.agentsHome.advancedMode;
};

export const selectSelectedAdditionalOrgIds = (state: RootState) => {
  return state.agentsHome.selectedAdditionalOrgIds;
};

export default agentsHomeSlice.reducer;
